import type React from "react";
import { ArrowDown, ArrowUp, Info } from "lucide-react";
import { BRAND } from "@/lib/colors";
import { Tooltip } from "@/components/ui/tooltip";

export interface KPITile {
  label: string;
  value: string;
  sub?: string;
  /** Methodology note shown in a hover tooltip next to the label, instead of taking up tile space like `sub`. */
  info?: string;
  color?: string;
  delta?: { pct: number; higherIsBetter: boolean } | null;
  /** This tile's value for the comparison period, shown alongside `compareLabel`. */
  compareValue?: string | undefined;
}

function Delta({
  pct,
  higherIsBetter,
}: {
  pct: number;
  higherIsBetter: boolean;
}): React.JSX.Element {
  const isPositive = pct >= 0;
  const isGood = higherIsBetter ? isPositive : !isPositive;
  const isNeutral = Math.abs(pct) < 0.5;
  const color = isNeutral ? "#aaa" : isGood ? BRAND.green : BRAND.red;
  const Icon = isPositive ? ArrowUp : ArrowDown;
  return (
    <span style={{ color }} className="inline-flex items-center gap-0.5 text-xs font-body font-medium ml-1">
      <Icon className="h-3 w-3" />
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

export function KPIGrid({
  tiles,
  compareLabel,
}: {
  tiles: KPITile[];
  /** e.g. "June 2026" — the period each tile's `compareValue` belongs to. */
  compareLabel?: string | null;
}): React.JSX.Element {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-[10px]">
      {tiles.map((tile, i) => (
        <div key={i} className="card-duress p-4">
          <div className="section-label mb-1 flex items-center gap-1">
            {tile.label}
            {tile.info && (
              <Tooltip
                content={tile.info}
                side="bottom"
                contentClassName="w-48 whitespace-normal text-center normal-case tracking-normal"
              >
                <Info className="h-3 w-3 text-muted-foreground/50 hover:text-muted-foreground cursor-help transition-colors" />
              </Tooltip>
            )}
          </div>
          <div className="flex items-baseline gap-1 mt-1">
            <div
              className="num text-[28px] leading-none font-semibold"
              style={tile.color ? { color: tile.color } : undefined}
            >
              {tile.value}
            </div>
            {tile.delta != null && (
              <Delta pct={tile.delta.pct} higherIsBetter={tile.delta.higherIsBetter} />
            )}

          </div>
          {compareLabel && tile.compareValue != null && (
            <div className="text-[11px] font-body text-muted-foreground mt-0.5">
              {compareLabel}: <span className="font-medium">{tile.compareValue}</span>
            </div>
          )}
          {tile.sub && (
            <div className="text-xs font-body text-muted-foreground mt-1">{tile.sub}</div>
          )}
        </div>
      ))}
    </div>
  );
}
